// @ts-nocheck
/*
 * Port of tts/tts-list.js (809 lines). BYTE-IDENTICAL.
 * TTS creations list with 4 modals:
 *   - single-language script viewer (with SSML pill-render + plain-text
 *     mirror + raw SSML details)
 *   - single-URL audio playback
 *   - delete confirmation
 *   - multi-language table (view each language script + play its URL)
 * `parseMultiEntries` handles the shape drift where `text_script` /
 * `prompt_url` may be JSON-serialised arrays.
 * PrimeReact DataTable â†’ obd3-gui DataTable + tanstack ColumnDef.
 * ToastNotification kept off (v1's toast state is used but its render
 * uses the react-toastify pipeline). SSML rendering via ported
 * SsmlScriptView + stripSsml helpers in the same folder.
 * Inline <style> tag with `.tts-chip / .tts-filename / .tts-view-btn`
 * classes preserved verbatim so the list matches v1 exactly.
 */
import React, { useState, useEffect } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { useNavigate } from 'react-router-dom';
import { FiTrash2 } from 'react-icons/fi';
import { FaPlay, FaEye } from 'react-icons/fa';
import { getApiCall, deleteApiCallWithHeader } from '@/service/httpCall';
import { API_TTS_LIST, API_TTS_DELETE } from '@/service/url';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import Loader from '@/components/partials/components/Loader';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getUser } from '@/utilities/setting';
import { USER_ROLES, MODULE_ACL, ROUTES } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import SsmlScriptView from './SsmlScriptView';
import { stripSsml } from './ssmlHelpers';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Download, Plus } from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const parseMultiEntries = (raw: any) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {
      return null;
    }
  }
  return null;
};

const TtsList = () => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState<any>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [webhooksData, setWebhooksData] = useState<any[]>([]);
  const [userRole, setUserRole] = useState<any>(undefined);
  const [playUrl, setPlayUrl] = useState<any>(null);
  const [playFileName, setPlayFileName] = useState('');
  const [scriptRow, setScriptRow] = useState<any>(null);
  const [multiLangRow, setMultiLangRow] = useState<any>(null);

  useEffect(() => {
    const resp = getUser();
    if (resp) {
      setUserRole(resp.role);
    }
  }, []);

  //  Fetch list
  useEffect(() => {
    setLoading(true);
    getApiCall(API_TTS_LIST, {})
      .then(onListSuccess)
      .catch(onError)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = async () => {
    try {
      setButtonDisable(true);

      exportToCSV({
        fileName: 'tts-list',
        data: webhooksData,
        columns: [
          { header: 'TTS Id', accessor: 'id' },
          {
            header: 'Username',
            accessor: (row: any) => row.tts_creator?.username || '-',
          },
          { header: 'Language', accessor: (row: any) => row.language || '-' },
          { header: 'Gender', accessor: (row: any) => row.gender || '-' },
          { header: 'File Name', accessor: (row: any) => row.file_name || '-' },
          {
            header: 'Text Script',
            accessor: (row: any) => {
              const parsed = parseMultiEntries(row.text_script);
              const s = parsed
                ? parsed.map((e: any) => e.text || '').join(' | ')
                : row.text_script || '';
              return stripSsml(s) || '-';
            },
          },
          {
            header: 'Total Length',
            accessor: (row: any) => row.character_count || '-',
          },
          {
            header: 'Request Date',
            accessor: (row: any) =>
              row.req_date
                ? new Date(row.req_date).toLocaleString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })
                : '-',
          },
          {
            header: 'Status',
            accessor: (row: any) => {
              if (row?.status == 5) return 'Not Confirmed';
              if (row?.tts_prompt?.prompt_status == '0') return 'Pending';
              if (row?.tts_prompt?.prompt_status == '1') return 'Approved';
              return 'Rejected';
            },
          },
          { header: 'Category', accessor: (row: any) => row.category || '-' },
          {
            header: 'Deleted Date',
            accessor: (row: any) =>
              row.deleted_date
                ? new Date(row.deleted_date).toLocaleString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })
                : '-',
          },
          {
            header: 'Deleted By',
            accessor: (row: any) => row.tts_deleted_details?.username || '-',
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const getLangEntries = (rowData: any) => {
    return parseMultiEntries(rowData?.tts_prompt?.prompt_url);
  };

  const promptStatusLabel = (s: any) => {
    if (s == 1)
      return {
        label: 'Approved',
        border: '#6ee7b7',
        fg: '#047857',
        bg: '#ecfdf5',
      };
    if (s == 2)
      return {
        label: 'Rejected',
        border: '#fca5a5',
        fg: '#b91c1c',
        bg: '#fef2f2',
      };
    return { label: 'Pending', border: '#fcd34d', fg: '#b45309', bg: '#fffbeb' };
  };

  const onListSuccess = (resp: any) => {
    setWebhooksData(resp);
  };

  const onError = (err: any) => {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
      toast.error(friendlyApiError(err) || 'Failed to load TTS list');
    } else {
      console.log(err);
    }
  };

  //   Delete TTS
  const confirmDelete = async () => {
    const url = API_TTS_DELETE.replace(':id', webhookToDelete);
    await deleteApiCallWithHeader(url).then(onDeleteSuccess).catch(onDeleteError);
    setShowDeleteModal(false);
    setWebhookToDelete(null);
  };

  const onDeleteSuccess = () => {
    toast.success('TTS deleted successfully');
    getApiCall(API_TTS_LIST, {}).then(onListSuccess).catch(onError);
  };

  const onDeleteError = (err: any) => {
    toast.error(friendlyApiError(err) || 'Failed to delete TTS');
  };

  const actionBodyTemplate = (rowData: any) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      {canDelete && (
        <FiTrash2
          style={{ color: '#dc3545', cursor: 'pointer' }}
          onClick={() => handleAction('delete', rowData.id)}
        />
      )}
    </div>
  );

  const handleAction = (action: string, id: any) => {
    if (action === 'delete') {
      setWebhookToDelete(id);
      setShowDeleteModal(true);
    }
  };

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    MODULE_ACL.TTS,
    'read',
  );
  const canDelete = hasPermission(
    moduleOperationsForReadPermissionCheck,
    MODULE_ACL.TTS,
    'delete',
  );
  const canAdd = userRole === USER_ROLES.CALL_CENTRE_ADMIN;

  if (!canRead) {
    return (
      <div
        className="text-center p-4"
        style={{ marginTop: '50px', marginBottom: '50px', fontSize: '16px' }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading) return <Loader />;

  const getStatusLabel = (rowData: any) => {
    const s = rowData?.tts_prompt?.prompt_status;
    if (rowData?.status == 5) return 'Not Confirmed';
    if (s == '0') return 'Pending';
    if (s == '1') return 'Approved';
    return 'Rejected';
  };

  const columns: ColumnDef<any>[] = [
    { accessorKey: 'id', header: 'TTS Id' },
    {
      id: 'username',
      header: 'Username',
      accessorFn: (row: any) => row?.tts_creator?.username || '',
      cell: ({ row }: any) => row.original?.tts_creator?.username || '-',
    },
    {
      id: 'language',
      header: 'Language',
      accessorFn: (row: any) => {
        const entries = getLangEntries(row);
        return entries && entries.length > 0
          ? entries.map((e: any) => e.language || '').join(' ')
          : row?.language || '';
      },
      cell: ({ row }: any) => {
        const entries = getLangEntries(row.original);
        const langs =
          entries && entries.length > 0
            ? entries.map((e: any) => String(e.language || '').toUpperCase())
            : [String(row.original?.language || '-').toUpperCase()];
        return <span className="tts-chip">{langs.join(', ')}</span>;
      },
    },
    {
      id: 'gender',
      header: 'Gender',
      accessorFn: (row: any) => row?.gender || '',
      cell: ({ row }: any) => {
        const g = String(row.original?.gender || '').toUpperCase();
        if (!g) return '-';
        return (
          <span
            className="tts-chip"
            style={{
              borderColor: g === 'MALE' ? '#93c5fd' : '#f9a8d4',
              color: g === 'MALE' ? '#1d4ed8' : '#be185d',
            }}
          >
            {g}
          </span>
        );
      },
    },
    {
      accessorKey: 'file_name',
      header: 'File Name',
      cell: ({ row }: any) => (
        <span className="tts-filename" title={row.original?.file_name || ''}>
          {row.original?.file_name || '-'}
        </span>
      ),
    },
    {
      id: 'text_script',
      header: 'Text Script',
      accessorFn: (row: any) => {
        const parsed = parseMultiEntries(row?.text_script);
        const scriptText = parsed
          ? parsed.map((e: any) => e.text || '').join(' ')
          : row?.text_script || '';
        return stripSsml(scriptText);
      },
      cell: ({ row }: any) => {
        const raw = row.original?.text_script;
        const parsedMulti = parseMultiEntries(raw);

        if (parsedMulti) {
          return (
            <button
              type="button"
              className="tts-view-btn"
              onClick={() => setMultiLangRow(row.original)}
            >
              <FaEye style={{ marginRight: '5px' }} />
              View ({parsedMulti.length} languages)
            </button>
          );
        }

        const script = typeof raw === 'string' ? raw : '';
        if (!script) return <span style={{ opacity: 0.6 }}>-</span>;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div className="tts-script-cell" style={{ flex: 1, minWidth: 0 }}>
              <SsmlScriptView value={script} clamp={2} />
            </div>
            <button
              type="button"
              className="tts-view-btn"
              onClick={() => setScriptRow(row.original)}
              title="View full script"
            >
              <FaEye />
            </button>
          </div>
        );
      },
    },
    { accessorKey: 'character_count', header: 'Total Length' },
    {
      accessorKey: 'req_date',
      header: 'Request Date',
      // Raw value is an unformatted timestamp — matching against it would
      // surface confusing hits (e.g. any digit query matches almost every
      // row via the raw ISO string), so it's excluded from the search box.
      enableGlobalFilter: false,
      cell: ({ row }: any) =>
        row.original?.req_date
          ? new Date(row.original?.req_date).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })
          : '-',
    },
    ...(userRole === USER_ROLES.SUPERADMIN
      ? [
          {
            id: 'status',
            header: 'Status',
            accessorFn: (row: any) => getStatusLabel(row),
            cell: ({ row }: any) => {
              const label = getStatusLabel(row.original);
              const cfg =
                label === 'Not Confirmed'
                  ? {
                      label,
                      border: '#d1d5db',
                      fg: '#4b5563',
                      bg: '#f3f4f6',
                    }
                  : label === 'Pending'
                  ? {
                      label,
                      border: '#fcd34d',
                      fg: '#b45309',
                      bg: '#fffbeb',
                    }
                  : label === 'Approved'
                  ? {
                      label,
                      border: '#6ee7b7',
                      fg: '#047857',
                      bg: '#ecfdf5',
                    }
                  : {
                      label,
                      border: '#fca5a5',
                      fg: '#b91c1c',
                      bg: '#fef2f2',
                    };
              return (
                <span
                  className="tts-chip"
                  style={{
                    borderColor: cfg.border,
                    color: cfg.fg,
                    background: cfg.bg,
                  }}
                >
                  {cfg.label}
                </span>
              );
            },
          } as ColumnDef<any>,
        ]
      : []),
    { accessorKey: 'category', header: 'Category' },
    {
      id: 'call_type',
      header: 'Call Type',
      accessorFn: (row: any) => {
        const vt = row?.tts_prompt?.voice_type;
        if (vt == 1) return 'OUTBOUND';
        if (vt == 2) return 'INBOUND';
        if (vt == 3) return 'BOTH';
        return '';
      },
      cell: ({ row }: any) => {
        const vt = row.original?.tts_prompt?.voice_type;
        if (vt == 1) return 'OUTBOUND';
        if (vt == 2) return 'INBOUND';
        if (vt == 3) return 'BOTH';
        return '-';
      },
    },
    {
      accessorKey: 'deleted_date',
      header: 'Deleted Date',
      // Same reasoning as Request Date — raw timestamp isn't a useful
      // search target and only produces noisy matches.
      enableGlobalFilter: false,
      cell: ({ row }: any) =>
        row.original?.deleted_date
          ? new Date(row.original.deleted_date).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })
          : '-',
    },
    {
      id: 'deleted_by',
      header: 'Deleted By',
      accessorFn: (row: any) => row?.tts_deleted_details?.username || '',
      cell: ({ row }: any) => row.original?.tts_deleted_details?.username || '-',
    },
    {
      id: 'play',
      header: 'Play',
      enableSorting: false,
      enableGlobalFilter: false,
      cell: ({ row }: any) => {
        const rowData = row.original;
        const entries = getLangEntries(rowData);
        if (entries && entries.length > 1) {
          return (
            <FaPlay
              style={{ color: '#7c3aed', cursor: 'pointer', fontSize: '1rem' }}
              title={`${entries.length} languages - click to view & play each`}
              onClick={() => setMultiLangRow(rowData)}
            />
          );
        }
        const singleUrl = entries?.[0]?.url || rowData?.tts_url;
        return singleUrl ? (
          <FaPlay
            style={{ color: '#7c3aed', cursor: 'pointer', fontSize: '1rem' }}
            onClick={() => {
              setPlayUrl(singleUrl);
              setPlayFileName(rowData.file_name || 'TTS Audio');
            }}
          />
        ) : (
          <>-</>
        );
      },
    },
    {
      id: 'action',
      header: 'Action',
      enableSorting: false,
      enableGlobalFilter: false,
      cell: ({ row }: any) => actionBodyTemplate(row.original),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="TTS Prompts"
        description="Text-to-speech prompts used across IVR and outbound campaigns."
        actions={
          <>
            {webhooksData.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={buttonDisable}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button onClick={() => navigate(`/${ROUTES.TTS.CREATE}`)}>
                <Plus className="h-4 w-4" />
                Add TTS
              </Button>
            )}
          </>
        }
      />
      <div className="webhooks-container">
        <Row>
          <Col sm="12">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                <div className="px-4 pb-4 pt-3">
                  <div
                    style={{ overflowX: 'auto' }}
                    className="broad-scrollbar"
                  >
                    <DataTable
                      tableId="tts-list"
                      columns={columns}
                      data={webhooksData || []}
                      pageSize={12}
                      emptyTitle="No TTS entries"
                      disableExport
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Single-language script view modal */}
        <Modal show={!!scriptRow} onHide={() => setScriptRow(null)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: '1rem' }}>
              Text Script
              {scriptRow?.file_name ? (
                <span
                  style={{ fontSize: '0.8rem', opacity: 0.65, marginLeft: '8px' }}
                >
                  {scriptRow.file_name}
                </span>
              ) : null}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div
              style={{
                border: '1px solid rgba(148, 163, 184, 0.3)',
                borderRadius: '8px',
                padding: '12px 14px',
                fontSize: '14px',
              }}
            >
              <SsmlScriptView value={scriptRow?.text_script || ''} />
            </div>

            <div
              style={{
                marginTop: '10px',
                display: 'inline-block',
                padding: '3px 10px',
                borderRadius: '999px',
                border: '1px solid rgba(148, 163, 184, 0.45)',
                fontSize: '12px',
                fontWeight: 600,
                color: '#475569',
              }}
            >
              Total Length: {stripSsml(scriptRow?.text_script || '').length}{' '}
              characters
            </div>

            <p className="mb-1 mt-3" style={{ fontSize: '12px', fontWeight: 600 }}>
              Plain text
            </p>
            <div
              style={{
                border: '1px solid rgba(148, 163, 184, 0.3)',
                borderRadius: '8px',
                padding: '10px 12px',
                fontSize: '13px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {stripSsml(scriptRow?.text_script || '') || '-'}
            </div>

            <details className="mt-3">
              <summary style={{ fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                Raw SSML
              </summary>
              <pre
                style={{
                  marginTop: '8px',
                  marginBottom: 0,
                  padding: '10px 12px',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {scriptRow?.text_script || '-'}
              </pre>
            </details>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setScriptRow(null)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Play Modal (single-language playback) */}
        <Modal show={!!playUrl} onHide={() => setPlayUrl(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title
              style={{
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FaPlay style={{ color: '#7c3aed' }} />
              {playFileName}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <audio controls autoPlay style={{ width: '100%' }}>
              <source src={playUrl} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </Modal.Body>
        </Modal>

        {/* Delete Modal */}
        <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this tts? This action cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={!!multiLangRow}
          onHide={() => setMultiLangRow(null)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: '1rem' }}>
              {multiLangRow?.file_name || 'Multi-language TTS'} -{' '}
              {getLangEntries(multiLangRow)?.length || 0} Languages
              {' · Total '}
              {(getLangEntries(multiLangRow) || []).reduce(
                (sum: number, e: any) => sum + (e.text_script || '').length,
                0,
              )}{' '}
              chars
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Language</th>
                  <th>Text Script</th>
                  <th>Total Length</th>
                  <th>Status</th>
                  <th>Play</th>
                </tr>
              </thead>
              <tbody>
                {(getLangEntries(multiLangRow) || []).map((entry: any, idx: number) => {
                  const cfg = promptStatusLabel(entry.status);
                  return (
                    <tr key={idx}>
                      <td>
                        <span className="tts-chip">
                          {String(entry.language || '-').toUpperCase()}
                        </span>
                      </td>
                      <td style={{ maxWidth: '320px' }}>
                        <SsmlScriptView value={entry.text_script || ''} clamp={2} />
                      </td>
                      <td>{(entry.text_script || '').length} chars</td>
                      <td>
                        <span
                          className="tts-chip"
                          style={{
                            borderColor: cfg.border,
                            color: cfg.fg,
                            background: cfg.bg,
                          }}
                        >
                          {cfg.label}
                        </span>
                      </td>
                      <td>
                        {entry.url ? (
                          <FaPlay
                            style={{ color: '#7c3aed', cursor: 'pointer' }}
                            onClick={() => {
                              setPlayUrl(entry.url);
                              setPlayFileName(entry.name || multiLangRow.file_name);
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Modal.Body>
        </Modal>
      </div>

      <style>
        {`
  .broad-scrollbar::-webkit-scrollbar {
    height: 8px;
  }
  .tts-chip {
    display: inline-block;
    padding: 2px 8px;
    border: 1px solid rgba(148, 163, 184, 0.45);
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }
  .tts-filename {
    display: inline-block;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }
  .tts-script-cell {
    cursor: pointer;
    border-radius: 6px;
    padding: 2px 4px;
    transition: background 0.15s ease;
  }
  .tts-script-cell:hover {
    background: rgba(124, 58, 237, 0.08);
  }
  .tts-view-btn {
    display: inline-flex;
    align-items: center;
    padding: 3px 9px;
    border: 1px solid rgba(124, 58, 237, 0.4);
    background: rgba(124, 58, 237, 0.06);
    color: #7c3aed;
    border-radius: 6px;
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
  }
  .tts-view-btn:hover {
    background: rgba(124, 58, 237, 0.14);
  }
`}
      </style>
    </FillPage>
  );
};

export default TtsList;
